import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { loadRecaptcha } from "../lib/recaptcha";

export const RecaptchaBlock = forwardRef(function RecaptchaBlock({ config, action = "login", hideLabel = false }, ref) {
  const v2Box = useRef(null);
  const v3Box = useRef(null);
  const v2Id = useRef(null);
  const v3Id = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!config?.enabled) return;
    let cancelled = false;
    loadRecaptcha().then((g) => {
      if (cancelled) return;
      if (config.v2_site_key && v2Box.current && v2Id.current === null) {
        v2Id.current = g.render(v2Box.current, { sitekey: config.v2_site_key, size: "normal" });
      }
      if (config.v3_site_key && v3Box.current && v3Id.current === null) {
        v3Id.current = g.render(v3Box.current, { sitekey: config.v3_site_key, size: "invisible" });
      }
    }).catch(() => setError("No se pudo cargar Google reCAPTCHA"));
    return () => { cancelled = true; };
  }, [config]);

  useImperativeHandle(ref, () => ({
    async getTokens() {
      const g = window.grecaptcha;
      const out = { v2: null, v3: null };
      if (v2Id.current !== null) out.v2 = g.getResponse(v2Id.current) || null;
      if (v3Id.current !== null) out.v3 = await g.execute(v3Id.current, { action });
      return out;
    },
    reset() {
      const g = window.grecaptcha;
      if (v2Id.current !== null) g.reset(v2Id.current);
      if (v3Id.current !== null) g.reset(v3Id.current);
    },
  }));

  if (!config?.enabled) return null;
  const label = config.mode === "both" ? "v2 y v3" : config.mode;
  return (
    <div className="flex flex-col items-center gap-3 pt-2" data-testid="recaptcha-block">
      {config.v2_site_key && <div ref={v2Box} data-testid="recaptcha-v2-widget" />}
      {config.v3_site_key && <div ref={v3Box} className="hidden" aria-hidden="true" data-testid="recaptcha-v3-widget" />}
      {error && <p className="text-xs text-red-600" data-testid="recaptcha-error">{error}</p>}
      {!hideLabel && (
        <p className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid="recaptcha-protected-label">
          <ShieldCheck size={12} className="text-emerald-600" /> Protegido por Google reCAPTCHA {label}
        </p>
      )}
    </div>
  );
});
